import api from '../config/api';

/**
 * Web Push client logic. Everything here talks to /api/admin/push/* (authenticated with the
 * dashboard JWT) and to the browser Push API. The VAPID public key comes from the server.
 *
 * States returned by getState():
 *   unsupported   – browser/device cannot do web push
 *   needs-install – iOS Safari tab: must be added to the Home Screen first
 *   unconfigured  – server has no VAPID keys configured
 *   denied        – user blocked notifications in browser/OS settings
 *   disabled      – supported, but this device is not subscribed for the signed-in user
 *   enabled       – this device is subscribed for the signed-in user
 */

export const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches === true || window.navigator.standalone === true;

function getSupport() {
  // iOS only exposes PushManager to Home Screen web apps, so a Safari tab must install first.
  if (isIos() && !isStandalone()) return 'needs-install';
  const ok =
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;
  return ok ? 'supported' : 'unsupported';
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function getRegistration() {
  const existing = await navigator.serviceWorker.getRegistration('/');
  return existing || navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
}

async function getCurrentSubscription() {
  const reg = await getRegistration();
  return reg.pushManager.getSubscription();
}

async function fetchPublicKey() {
  try {
    const { data } = await api.get('/api/admin/push/public-key');
    return data.publicKey;
  } catch (err) {
    if (err.response?.status === 503) return null; // server not configured
    throw err;
  }
}

const pushService = {
  getSupport,

  async getState() {
    const support = getSupport();
    if (support !== 'supported') return support;
    if (Notification.permission === 'denied') return 'denied';

    try {
      const sub = await getCurrentSubscription();
      if (!sub) {
        const key = await fetchPublicKey();
        return key ? 'disabled' : 'unconfigured';
      }
      const { data } = await api.post('/api/admin/push/status', { endpoint: sub.endpoint });
      if (!data.configured) return 'unconfigured';
      return data.subscribed ? 'enabled' : 'disabled';
    } catch {
      return 'disabled';
    }
  },

  /** Must be called directly from a click handler (iOS requires a user gesture for the permission prompt). */
  async enable() {
    const support = getSupport();
    if (support !== 'supported') throw new Error(support);

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'disabled';

    const publicKey = await fetchPublicKey();
    if (!publicKey) return 'unconfigured';

    const reg = await getRegistration();
    await navigator.serviceWorker.ready;

    const applicationServerKey = urlBase64ToUint8Array(publicKey);
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
    }

    try {
      const json = sub.toJSON();
      await api.post('/api/admin/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
    } catch (err) {
      await sub.unsubscribe().catch(() => {});
      throw err;
    }
    return 'enabled';
  },

  async disable() {
    const sub = await getCurrentSubscription();
    if (!sub) return 'disabled';
    try {
      await api.post('/api/admin/push/unsubscribe', { endpoint: sub.endpoint });
    } finally {
      await sub.unsubscribe().catch(() => {});
    }
    return 'disabled';
  },

  async sendTest() {
    const { data } = await api.post('/api/admin/push/test');
    return data;
  },

  /** Best effort, called before the auth token is discarded so the next login must opt in again. */
  async cleanupOnLogout() {
    if (getSupport() !== 'supported' || !localStorage.getItem('authToken')) return;
    const work = (async () => {
      const sub = await getCurrentSubscription();
      if (!sub) return;
      await api.post('/api/admin/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    })();
    await Promise.race([work, new Promise((resolve) => setTimeout(resolve, 3000))]).catch(() => {});
  },
};

export default pushService;
